// The change-feed loop: a port of `SyncEngine.pullLocked` / `apply` / `prune` (GroundTeam/Sync/SyncEngine.swift:426-523)
// with the merge-on-arrival rule of `SyncReplica.apply` (SyncReplica.swift:259-293), WEB_PLAN.md §3.3.
//
// Differences from iOS, on purpose:
// - The first pull of an empty replica (cursor 0) asks for `p_full = true`, so the server skips tombstones and rows
//   the caller cannot read instead of sending them only to be dropped (§3.3). iOS starts incremental from 0; for an
//   empty replica the result is the same.
// - Page size 1000 (the server's cap, M1:1224) instead of iOS's 500: fewer round trips for a first load.

import { RECORD_DEPENDENTS, SYNC_COLLECTIONS, type Collection } from '@/contracts/collections';
import { idKey, sameId } from '@/contracts/uuid';
import type { PullResult, Row } from '@/contracts/wire';
import { toEnvelopeDate } from '@/contracts/wireDate';
import { canonicalPayload, keepsMergeBase } from './canonical';
import { deepEqual, threeWay } from './merge';
import type { OutboxEntry, Replica } from './replica';
import { itemKey, localIsNewer, toCachedRow, type SyncRuntime } from './runtime';

export const PULL_PAGE_SIZE = 1000;

export interface PullOutcome {
  /** Local rows added, changed or removed (outbox merges included). */
  changed: number;
  /** A full resync ran (first load or a visibility epoch change). */
  full: boolean;
  /** The runtime was stopped midway; nothing more was written. */
  stopped: boolean;
}

export interface PullHooks {
  /** A full resync is starting (show "Updating visibility…" / first-load progress). */
  onFullStart?: () => void;
  /** After each applied page: rows applied so far in this pull. */
  onPage?: (applied: number) => void;
}

interface ItemName {
  collection: Collection;
  id: string;
}

/** Author key of each record dependent (SyncReplica.dependentNames). */
const DEPENDENT_AUTHOR: Partial<Record<Collection, string>> = {
  activities: 'userId',
  comms: 'userId',
  attachments: 'uploadedBy',
  enrollments: 'enrolledBy',
};

/**
 * One pull: pages through the feed after the cursor until `has_more` is false. Errors propagate (the engine maps
 * them); everything applied before the error stays applied and persisted.
 */
export async function pullOnce(rt: SyncRuntime, hooks: PullHooks = {}): Promise<PullOutcome> {
  const replica = rt.replica;
  const outcome: PullOutcome = { changed: 0, full: false, stopped: false };
  if (rt.isStopped()) return { ...outcome, stopped: true };

  let full = replica.meta.cursor === 0;
  let after = replica.meta.cursor;
  let seen = new Set<string>();
  let scanEpoch: number | null = null;
  if (full) hooks.onFullStart?.();

  while (!rt.isStopped()) {
    const page: PullResult = await rt.api.pull(rt.companyId, after, PULL_PAGE_SIZE, full);
    if (rt.isStopped()) break;
    if (scanEpoch === null) {
      scanEpoch = page.epoch;
      if (!full && replica.meta.epoch !== null && page.epoch !== replica.meta.epoch) {
        // Visibility rules changed (territory reassignment, role change, repsSeeOnlyOwnRecords): re-read everything
        // visible and drop what is not.
        full = true;
        after = 0;
        seen = new Set();
        hooks.onFullStart?.();
        continue;
      }
    }
    outcome.changed += applyPage(rt, page, full, seen);
    const previous = after;
    after = page.next;
    replica.updateMeta({ cursor: page.next });
    await replica.persist();
    hooks.onPage?.(outcome.changed);
    if (!page.has_more) break;
    if (page.next <= previous && page.items.length === 0 && page.revoked.length === 0) break; // no progress
  }
  outcome.full = full;
  if (rt.isStopped()) return { ...outcome, stopped: true };

  if (full) outcome.changed += prune(replica, seen);
  const meta: Parameters<Replica['updateMeta']>[0] = { initialFetchDone: true, lastSyncedAt: new Date().toISOString() };
  if (scanEpoch !== null) meta.epoch = scanEpoch;
  replica.updateMeta(meta);
  await replica.persist();
  return outcome;
}

/** Applies one page (SyncEngine.apply). Returns the number of local changes. */
export function applyPage(rt: SyncRuntime, page: PullResult, full: boolean, seen: Set<string>): number {
  const upserts: Row[] = [];
  const deletions: ItemName[] = [];
  const visible = new Set<string>();
  for (const row of page.items) {
    const key = itemKey(row.collection, row.id);
    if (full) seen.add(key);
    if (row.deleted) {
      deletions.push(row);
    } else {
      visible.add(key);
      upserts.push(row);
    }
  }
  if (page.revoked.length > 0) {
    // Records the caller can no longer see take their activities/comms/attachments/enrollments along, except the
    // caller's own (still visible to them) and rows this same page shows as visible.
    const revokedRecords = new Set(page.revoked.filter((r) => r.collection === 'records').map((r) => idKey(r.id)));
    const dependents = dependentNames(rt.replica, revokedRecords, rt.profileId).filter((n) => !visible.has(itemKey(n.collection, n.id)));
    const removed = new Set(deletions.map((d) => itemKey(d.collection, d.id)));
    for (const name of [...page.revoked, ...dependents]) {
      const key = itemKey(name.collection, name.id);
      if (removed.has(key) || visible.has(key)) continue;
      removed.add(key);
      deletions.push(name);
    }
  }
  let changed = 0;
  for (const row of upserts) if (applyServerRow(rt, row)) changed++;
  for (const name of deletions) if (removeLocal(rt.replica, name.collection, name.id)) changed++;
  return changed;
}

/**
 * Stores a live server row. With a pending local edit the row is merged into it right away, as iOS does on every
 * pull: the entry's base becomes this server version, a both-sides change goes to the newer side, and an edit the
 * server already matches is dropped. The visible row keeps showing the local payload until the push confirms it.
 */
export function applyServerRow(rt: SyncRuntime, row: Row): boolean {
  const replica = rt.replica;
  const cached = toCachedRow(row);
  if (!cached || !row.payload) return false; // no object payload: left untouched (undecodable)
  let changed = replica.putRow(row.collection, cached);
  const entry = replica.getEntry(row.collection, row.id);
  if (!entry) return changed;

  const server = canonicalPayload(row.collection, row.payload);
  const base = keepsMergeBase(row.collection) ? server : null;
  if (entry.kind === 'delete') {
    // Deleted here while edited elsewhere: the delete stands, now based on this version, and is queued again even
    // after an earlier refusal (SyncReplica.apply keptLocalDeletes → SyncEngine.resolve sets it pending again).
    if (entry.baseVersion !== row.version || entry.state !== 'pending') {
      const next: OutboxEntry = { ...entry, basePayload: base, baseVersion: row.version, state: 'pending' };
      delete next.error;
      replica.putEntry(next);
      return true;
    }
    return changed;
  }
  const merged = threeWay(
    keepsMergeBase(row.collection) ? entry.basePayload : null,
    entry.localPayload ?? {},
    server,
    localIsNewer(entry.modifiedAt, row.modified_at),
  );
  if (deepEqual(merged, server)) {
    replica.removeEntry(row.collection, row.id); // the server already has it (adoptedOverLocal)
  } else {
    // needsUpload: iOS re-stamps the change (tracker.touch) and queues it again, also after an 'invalid' refusal.
    const next = {
      ...entry,
      basePayload: base,
      baseVersion: row.version,
      localPayload: merged,
      modifiedAt: toEnvelopeDate(rt.now()),
      state: 'pending' as const,
    };
    delete next.error;
    replica.putEntry(next);
  }
  changed = true;
  return changed;
}

/** Removes a row and its outbox entry (tombstones and revocations win over local edits). */
export function removeLocal(replica: Replica, collection: Collection, id: string): boolean {
  const row = replica.removeRow(collection, id);
  const entry = replica.removeEntry(collection, id);
  return row || entry;
}

/**
 * Local activities/comms/attachments/enrollments whose `recordId` is one of `recordIds` (lower-case) and whose author
 * is not `profileId` (SyncReplica.dependentNames). Looks at what the screens show: pending saves (including items
 * never pushed) count with their local payload; items with a pending delete are already gone locally.
 */
export function dependentNames(replica: Replica, recordIds: Set<string>, profileId: string | null): ItemName[] {
  if (recordIds.size === 0) return [];
  const mine = (author: unknown) => profileId !== null && typeof author === 'string' && sameId(author, profileId);
  const names: ItemName[] = [];
  for (const collection of RECORD_DEPENDENTS) {
    const authorKey = DEPENDENT_AUTHOR[collection] ?? 'userId';
    const consider = (id: string, payload: Record<string, unknown> | null) => {
      if (!payload) return;
      const recordId = payload.recordId;
      if (typeof recordId !== 'string' || !recordIds.has(idKey(recordId))) return;
      if (mine(payload[authorKey])) return;
      names.push({ collection, id });
    };
    for (const row of replica.serverRows(collection)) consider(row.id, replica.effectivePayload(collection, row.id));
    for (const entry of replica.entriesOf(collection)) {
      if (replica.getRow(collection, entry.id)) continue; // handled above
      if (entry.kind === 'save') consider(entry.id, entry.localPayload);
    }
  }
  return names;
}

/**
 * After a full pass: removes rows the server no longer shows this caller, except the settings singleton and rows with
 * an outbox entry (their edit has not gone up yet) (SyncEngine.prune).
 */
export function prune(replica: Replica, seen: Set<string>): number {
  const stale: ItemName[] = [];
  for (const collection of SYNC_COLLECTIONS) {
    if (collection === 'settings') continue;
    for (const row of replica.serverRows(collection)) {
      if (seen.has(itemKey(collection, row.id))) continue;
      if (replica.getEntry(collection, row.id)) continue;
      stale.push({ collection, id: row.id });
    }
  }
  let removed = 0;
  for (const name of stale) if (replica.removeRow(name.collection, name.id)) removed++;
  return removed;
}
